import React, { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { animate, motion, useMotionValue, useReducedMotion } from 'framer-motion';
import { RoboticEyeScene } from './RoboticEyeScene';
import { pinnedCenterY } from '../hooks/useRoboticEyeScene';
import { RobotChat } from './RobotChat';
import { DockChat } from './DockChat';
import { CinemaOverlay } from './CinemaOverlay';
import { QuestHud } from './QuestHud';
import { QuestFinale } from './QuestFinale';
import { createPortal } from 'react-dom';
import { useQuest } from '../hooks/useQuest';
import { quest } from '../utils/quest';
import { useQuestPilot } from '../hooks/useQuestPilot';
import { FINALE_AGAIN, FINALE_START } from '../data/chatScript';
import type { BubbleLink, ChatPhase, ChatSignal, CinemaLink } from '../types/chat';
import type { Emotion, EmotionMode } from '../types/emotion';
import type { FaceSide } from '../utils/droneFace';
import type { ArmsMode } from '../utils/droneArms';
interface DroneCompanionProps {
  /** Hero: the drone floats here on load and simply scrolls away with it (no chat, no docking) */
  heroRef: RefObject<HTMLElement>;
  /** Spot under the showreel where the drone sneaks in, chats, and later docks from */
  slotRef: RefObject<HTMLElement>;
  /** Once this element is scrolled well up, the drone moves to the slot */
  triggerRef: RefObject<HTMLElement>;
  /** Second spot after the work: he jumps out of the corner into it and starts a different conversation */
  finaleSlotRef: RefObject<HTMLElement>;
  /** Footer spot: once the footer is half in view he jumps out of the corner into its middle */
  footerSlotRef: RefObject<HTMLElement>;
  modelUrl: string;
  emotion: EmotionMode;
  screenScale: number;
  faceSide: FaceSide;
  armsMode: ArmsMode;
}

/** Chat starts this long after he has settled into the section spot (ms) */
const CHAT_DELAY = 900;
/** In the finale he flies in from the corner first, so he waits a little longer before talking */
const FINALE_CHAT_DELAY = 1700;
/** Finale spot takes over once its middle has risen to this fraction of the screen height */
const FINALE_AT = 0.8;
/** Footer spot takes over once the footer's top edge has risen to this fraction of the screen */
const FOOTER_AT = 0.5;

/**
 * Pixel + his chat, in a page-wide layer.
 * Hero: floats, rides along with the page, stays quiet.
 * Past the "We build brands…" line: sneaks in from the top to his section spot, starts chatting 3s later.
 * Past that section: flies to the bottom-right corner as a small launcher.
 */
export function DroneCompanion({
  heroRef,
  slotRef,
  triggerRef,
  finaleSlotRef,
  footerSlotRef,
  modelUrl,
  emotion,
  screenScale,
  faceSide,
  armsMode
}: DroneCompanionProps) {
  const reduce = useReducedMotion();
  const [docked, setDocked] = useState(false);
  const [dockLanded, setDockLanded] = useState(false);
  const [, setBubblesVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [arrived, setArrived] = useState(false);
  const [stage, setStage] = useState<'hero' | 'section' | 'finale' | 'footer'>('hero');
  const [chat, setChat] = useState<ChatSignal>({
    phase: 'idle',
    emotion: 'neutral',
    pulse: 0
  });

  // Quest mode: Pixel leaves whatever spot he's in and sticks to the corner, whatever you scroll
  const questState = useQuest();
  const questOn = questState.status === 'active';
  const [questHudBusy, setQuestHudBusy] = useState(false);
  const questDockRef = useRef<HTMLDivElement>(null);
  const questRoamRef = useRef<HTMLDivElement>(null);
  const pilot = useQuestPilot();
  const onPage = pilot.mode === 'page';
  /** Out of the corner: either a fixed spot on screen, or (the footer) a spot on the page itself */
  const roaming = pilot.mode === 'roam' || onPage;
  const questPageRef = useRef<HTMLDivElement>(null);
  const pageLeft = useMotionValue(0);
  const pageTop = useMotionValue(0);
  const wasOnPage = useRef(false);
  const pageX = pilot.mode === 'page' ? pilot.x : 0;
  const pageY = pilot.mode === 'page' ? pilot.docY : 0;
  const pageSpeed = pilot.mode === 'page' ? pilot.speed : 0;
  const celebrating = pilot.mode === 'page' && !!pilot.celebrate;

  // Celebrating (the flips and spins themselves are played by the 3D scene): a big grin
  useEffect(() => {
    const el = questPageRef.current;
    if (!el || !celebrating) return;
    el.dataset.droneEmotion = 'happy';
    el.dataset.droneGrin = '1';
    return () => {
      delete el.dataset.droneEmotion;
      delete el.dataset.droneGrin;
    };
  }, [celebrating]);

  // Lift-off: his own face, beaming — a huge grin with heart eyes flickering in, chattering away
  const liftingOff = questOn && questState.step === 'finale' && questState.finaleStage === 'rise';
  useEffect(() => {
    const el = questRoamRef.current;
    if (!el || !liftingOff) return;
    el.dataset.droneEmotion = 'happy';
    el.dataset.droneGrin = '1';
    let heart = false;
    const id = window.setInterval(() => {
      heart = !heart;
      el.dataset.droneHeart = heart ? '1' : '0';
    }, 380);
    return () => {
      window.clearInterval(id);
      delete el.dataset.droneEmotion;
      delete el.dataset.droneGrin;
      delete el.dataset.droneHeart;
    };
  }, [liftingOff]);
  useEffect(() => {
    if (!onPage) {
      wasOnPage.current = false;
      return;
    }
    const vw = window.innerWidth;
    // Setting off (from the corner or from fetching you): start the page spot right where he is,
    // so his first flight heads off from there instead of looping in from the document top
    if (!wasOnPage.current) {
      wasOnPage.current = true;
      const p = quest.pixelPx;
      pageLeft.set(p.x > -9000 ? p.x : vw * 0.85);
      pageTop.set(window.scrollY + (p.x > -9000 ? p.y : window.innerHeight * 0.8));
    }
    const ease = [0.65, 0, 0.35, 1] as const;
    const d = reduce ? 0 : pageSpeed;
    const a = animate(pageLeft, vw * pageX, {
      duration: d,
      ease
    });
    const b = animate(pageTop, pageY, {
      duration: d,
      ease
    });
    return () => {
      a.stop();
      b.stop();
    };
  }, [onPage, pageX, pageY, pageSpeed, reduce, pageLeft, pageTop]);

  // The element the 3D scene treats as home — read every frame, so switching it moves the drone
  const activeAnchor = useRef<HTMLElement | null>(null);
  useEffect(() => {
    activeAnchor.current = questOn ? onPage ? questPageRef.current : roaming ? questRoamRef.current : questDockRef.current : stage === 'footer' ? footerSlotRef.current : stage === 'finale' ? finaleSlotRef.current : stage === 'section' ? slotRef.current : heroRef.current;
  }, [stage, questOn, roaming, onPage, heroRef, slotRef, finaleSlotRef, footerSlotRef]);

  // Hero ↔ section spot: Pixel stays pinned where he sits in the hero until the section spot
  // scrolls up under him, then he rides along with it (and is released again on the way back up)
  useEffect(() => {
    const check = () => {
      const hero = heroRef.current;
      const slot = slotRef.current;
      if (!hero || !slot || !triggerRef.current) return;
      const hr = hero.getBoundingClientRect();
      const pinnedY = pinnedCenterY(hr, window.innerHeight);
      const sr = slot.getBoundingClientRect();
      const slotY = sr.top + sr.height / 2;
      // After the work: once the finale spot is on screen he leaves the corner for it
      // (switching home while docked makes him fly out of the corner; scrolling back reverses it)
      const fr = finaleSlotRef.current?.getBoundingClientRect();
      const finaleY = fr ? fr.top + fr.height / 2 : Infinity;
      // (once you scroll past the finale scene he rides away with it, then jumps into the corner)
      // Footer roughly half in view: he jumps out of the corner into its middle and stays there.
      // Scrolling back out hands him to the finale spot, which is long scrolled past → straight back to the corner
      const footer = footerSlotRef.current?.closest('footer');
      const footerTop = footer ? footer.getBoundingClientRect().top : Infinity;
      if (footerTop <= window.innerHeight * FOOTER_AT) setStage('footer');else if (fr && finaleY <= window.innerHeight * FINALE_AT) setStage('finale');
      // Phones: hand over to the section spot (and start the chat) 15% of the screen height sooner
      else {
        const lead = window.innerWidth < 768 ? window.innerHeight * 0.15 : 0;
        setStage(slotY <= pinnedY + lead ? 'section' : 'hero');
      }
    };
    check();
    window.addEventListener('scroll', check, {
      passive: true
    });
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [triggerRef, heroRef, slotRef, finaleSlotRef, footerSlotRef]);

  // Written every frame by the 3D scene — motion values, so no re-renders
  const bubbleX = useMotionValue(-9999);
  const bubbleY = useMotionValue(-9999);
  const floatX = useMotionValue(0);
  const floatY = useMotionValue(0);
  const bubbleLink = useRef<BubbleLink>({
    x: bubbleX,
    y: bubbleY,
    dx: floatX,
    dy: floatY,
    hovered: false
  }).current;

  // Full-charge movie mode
  const cinemaDark = useMotionValue(0);
  const cinemaBars = useMotionValue(0);
  const cinema = useRef<CinemaLink>({
    dark: cinemaDark,
    bars: cinemaBars
  }).current;
  const handleChatPhase = useCallback((phase: ChatPhase, chatEmotion: Emotion) => {
    setChat(c => c.phase === phase && c.emotion === chatEmotion ? c : {
      ...c,
      phase,
      emotion: chatEmotion
    });
  }, []);
  const handleChatPulse = useCallback(() => setChat(c => ({
    ...c,
    pulse: c.pulse + 1
  })), []);

  // Quest finished: he launched home — the site carries on without him until it's reloaded
  if (questState.status === 'done' && quest.hasDeparted()) return null;
  return <>
      {/* Quest home: a dock spot that's always "scrolled past", so he stays in the corner */}
      <div ref={questDockRef} aria-hidden="true" data-drone-dock="true" className="pointer-events-none fixed left-0 top-[-400px] h-px w-px" data-id="element-73" data-mp="1jkbvbdaj0dey" />
      {/* Quest flight spot: a fixed point on screen that glides around; Pixel flies wherever it goes,
          independent of the scroll — always at his normal (hero) size once he's out of the corner */}
      <motion.div ref={questRoamRef} aria-hidden="true" data-drone-keep-scale="true"
    // Faces you square-on while flying, so the sign on his screen stays readable
    data-drone-stare="1" className="pointer-events-none fixed h-px w-px -translate-x-1/2 -translate-y-1/2" initial={false} animate={pilot.mode === 'roam' ? {
      left: `${pilot.x * 100}vw`,
      top: pilot.ys && !reduce ? pilot.ys.map(y => `${y * 100}vh`) : `${pilot.y * 100}vh`
    } : {
      left: '50vw',
      top: '50vh'
    }} transition={reduce ? {
      duration: 0
    } : pilot.mode === 'roam' ? {
      duration: pilot.speed,
      // Lift-off: slow climb that keeps accelerating until he's shot off the top
      ease: pilot.accelerate ? [0.55, 0, 0.9, 0.35] : [0.65, 0, 0.35, 1],
      times: pilot.times
    } : {
      duration: 0.6,
      ease: [0.65, 0, 0.35, 1]
    }} data-id="element-74" data-mp="10tkddnch3eoy" />
      {/* Quest footer spot: lives on the page (absolute, in document coordinates), so it scrolls away
          with the page — he waits in the middle of the footer however you scroll.
          Phones: talking, he has his first-chat size; for the timing game and the charge he's bigger,
          so his face is easy to read and tap */}
      {createPortal(<motion.div ref={questPageRef} aria-hidden="true" data-drone-keep-scale="true" data-drone-stare="1" data-drone-mobile-scale={questState.finaleStage === 'qte' || questState.finaleStage === 'charge' ? '0.95' : '0.62'} className="pointer-events-none absolute h-px w-px -translate-x-1/2 -translate-y-1/2" style={{
      left: pageLeft,
      top: pageTop
    }} data-id="element-75" data-mp="391pgehgpftke" />, document.body)}
      <CinemaOverlay link={cinema} data-id="element-76" data-mp="1lzimd19j2mjz" />
      <RoboticEyeScene anchorRef={activeAnchor} onDockChange={setDocked} onDockLanded={setDockLanded} onArrive={setArrived} bubbleLink={bubbleLink} onBubblesVisible={setBubblesVisible} onBusyChange={setBusy} cinema={cinema} modelUrl={modelUrl} emotion={emotion} screenScale={screenScale} faceSide={faceSide} armsMode={armsMode} chat={chat} data-id="element-77" data-mp="1lwmqdih8q7y6" />
      <RobotChat reduce={!!reduce} hostRef={slotRef} active={stage === 'section' && arrived && !questOn} startDelay={CHAT_DELAY} docked={docked} busy={busy} bubbleLink={bubbleLink} onPhase={handleChatPhase} onPulse={handleChatPulse} data-id="element-78" data-mp="29pmp76ew6i9x" />
      {/* Finale scene: a different conversation, rendered in the second spot */}
      <RobotChat reduce={!!reduce} hostRef={finaleSlotRef} active={stage === 'finale' && arrived && !questOn} startDelay={FINALE_CHAT_DELAY} docked={docked} busy={busy} bubbleLink={bubbleLink} onPhase={handleChatPhase} onPulse={handleChatPulse} startNode={FINALE_START} againNode={FINALE_AGAIN} data-id="element-79" data-mp="3q5sfi2hrjfq1" />
      <DockChat reduce={!!reduce} landed={docked && dockLanded && !questOn && !questHudBusy} onPhase={handleChatPhase} onPulse={handleChatPulse} data-id="element-80" data-mp="cxfpb1rv7t9g" />
      <QuestHud reduce={!!reduce} onPhase={handleChatPhase} onPulse={handleChatPulse} onBusyChange={setQuestHudBusy} inCorner={questOn && !roaming && docked && dockLanded} data-id="element-81" data-mp="15f1sbsrfhljb" />
      <QuestFinale onPhase={handleChatPhase} onPulse={handleChatPulse} data-id="element-82" data-mp="k795v1bb5tvt" />
    </>;
}